import { useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Modal } from '../../components/atoms/Modal'
import { Field } from '../../components/molecules/Field'
import type { WebhookProviderDto } from '../../../viewmodels/useWebhookProviders'

const SECRET_MIN = 8
const SECRET_MAX = 256

interface Props {
  readonly open: boolean
  readonly provider: WebhookProviderDto
  readonly onClose: () => void
  readonly onSubmit: (value: string) => Promise<{ ok: true } | { ok: false; message: string }>
}

/**
 * Secret edit modal — write-only path. The current value is NEVER
 * shown; we display only the fingerprint we already have. Typing a
 * new value + Save rotates it. Kiran 2026-06-18.
 */
export function WebhookProviderSecretModal({ open, provider, onClose, onSubmit }: Props): JSX.Element {
  const [value, setValue] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (): Promise<void> => {
    setError(null)
    if (value.length < SECRET_MIN) {
      setError(`Secret must be at least ${SECRET_MIN} characters`)
      return
    }
    if (value.length > SECRET_MAX) {
      setError(`Secret exceeds ${SECRET_MAX} characters`)
      return
    }
    setSubmitting(true)
    const res = await onSubmit(value)
    setSubmitting(false)
    if (!res.ok) {
      setError(res.message)
    } else {
      onClose()
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${provider.has_secret ? 'Rotate' : 'Set'} signing secret · ${provider.display_name}`}
      maxWidth="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting || value.length < SECRET_MIN}>
            {submitting ? 'Saving…' : provider.has_secret ? 'Rotate' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error !== null ? <Alert tone="error">{error}</Alert> : null}
        {provider.has_secret ? (
          <p className="font-body text-body-sm text-text-secondary">
            Current value (masked):{' '}
            <span className="font-mono text-body-xs text-charcoal">{provider.secret_fingerprint}</span>
            . Typing a new value below and clicking Rotate will overwrite it.
          </p>
        ) : (
          <p className="font-body text-body-sm text-text-secondary">
            No secret set yet. Paste the provider&apos;s HMAC signing secret below.
          </p>
        )}
        <Field
          label="New secret"
          helper={`${SECRET_MIN}–${SECRET_MAX} characters. Stored in CONFIG_KV; never returned by any read.`}
          error={null}
        >
          <Input
            type="password"
            autoComplete="new-password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="paste secret"
            maxLength={SECRET_MAX}
          />
        </Field>
      </div>
    </Modal>
  )
}
